import { Observable, first, map } from 'rxjs';
import {
  PunchoutBuildContext,
  PunchoutCustomerConfig,
  PunchoutOrderRow,
  PunchoutPayload,
  PunchoutProtocol
} from '../../../models/punchout';
import { PunchoutCurrentConfigService } from '../punchout-current-config.service';
import { PunchoutProtocolHandler } from './punchout-protocol.handler';

/**
 * Lazy-resolving protocol handler. Defers the mapper choice until first use,
 * consulting `/json/punchout/current-config` for the logged-in user's binding
 * and matching it against the `PUNCHOUT_PROTOCOL_HANDLERS` registry by
 * `(protocol, identifier)`.
 *
 * If the user's config has no `qualifier` (legacy data) we fall back to the
 * first registered handler for the protocol, then to the first registered
 * handler overall. Callers MUST invoke `resolve()` before `buildPayload()`;
 * `PunchoutFormComponent` does this in ngOnInit before assembling the payload.
 */
export class RuntimeProtocolDelegate extends PunchoutProtocolHandler {
  protocol: PunchoutProtocol = 'oci';
  identifier = 'runtime-delegate';
  label = 'Runtime delegate';

  private resolved?: PunchoutProtocolHandler;
  private currentConfig: PunchoutCustomerConfig | null = null;

  constructor(
    private handlers: PunchoutProtocolHandler[],
    private currentConfigService: PunchoutCurrentConfigService
  ) {
    super();
  }

  resolve(): Observable<PunchoutProtocolHandler> {
    return this.currentConfigService.getCurrentConfig().pipe(
      first(),
      map(cfg => {
        this.currentConfig = cfg ?? null;
        const proto = cfg?.format ?? 'oci';
        const handler = this.pickHandler(proto, cfg?.qualifier);
        this.protocol = handler.protocol;
        this.resolved = handler;
        return handler;
      })
    );
  }

  getCurrentConfig(): PunchoutCustomerConfig | null {
    return this.currentConfig;
  }

  override prepare(): Observable<void> {
    if (!this.resolved) {
      throw new Error('Punchout handler not yet resolved — call resolve() first.');
    }
    return this.resolved.prepare();
  }

  buildPayload(rows: PunchoutOrderRow[], context?: PunchoutBuildContext): PunchoutPayload {
    if (!this.resolved) {
      throw new Error('Punchout handler not yet resolved — call resolve() first.');
    }
    return this.resolved.buildPayload(rows, context);
  }

  private pickHandler(protocol: PunchoutProtocol, qualifier?: string): PunchoutProtocolHandler {
    if (qualifier) {
      const byIdentifier = this.handlers.find(
        h => h.protocol === protocol && h.identifier === qualifier
      );
      if (byIdentifier) {
        return byIdentifier;
      }
    }
    const byProtocol = this.handlers.find(h => h.protocol === protocol);
    if (byProtocol) {
      return byProtocol;
    }
    if (!this.handlers.length) {
      throw new Error('No punchout protocol handlers registered.');
    }
    return this.handlers[0];
  }
}
